import { isDrawerPanelView } from '@/shell/drawerPanel';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { CheckCircle2, MailWarning } from 'lucide-react';
import { useStores } from '@/hooks/useStores';
import { Button } from '@/components/Button';
import { BOTTOM_BAR_ROW, ViewportBottomBar } from '@/components/AppViewport';
import { BottomScrollFade, useBottomScrollFade } from '@/components/BottomScrollFade';
import { authApi, type IMailStanding } from '@/services/AuthApi';
import { actionFailureTextIn } from '@/services/actionFailure';
import { useT } from '@/i18n/LocaleProvider';
import { isStoppedStanding } from './shellBannerNeeds';
import { useFormat } from '@/i18n/useFormat';
import { registerEnglishArea } from '@/i18n/catalogue';
import { banners } from '@featit/shared/i18n/messages/en/banners';

registerEnglishArea('banners', banners);

/**
 * "Mail to your address bounced" (I-1395, RT-26).
 *
 * A permanent bounce stops every message to the address, the password reset included. Without this
 * the person just stops getting mail and never learns why. It says which address, what that stops and
 * the way out: fix the mailbox, then "Send again". A complaint is different and says so: nothing in
 * the app lifts it, only an administrator can.
 *
 * Rendered once above the routes beside `EmailVerificationBanner`, which it follows. It reads
 * `/auth/me`'s `mailStanding` (I-1405) and, for a session that did not carry it, asks
 * `/auth/me/mail-standing` once per signed-in account, and renders nothing when mail is not stopped,
 * on the public screens and for a verification session, which has no address of its own.
 */
/**
 * The address as one unit (RT-26a, I-1822): drawn inline in the sentence, a hyphen in it is a place
 * the line may break, so `qa-joker-` / `wba@…` read as two words at 390. Its own box moves it to the
 * next line whole when it fits there, and breaks it only where a longer one would run past the
 * banner.
 */
function MailAddress({ children }: { children: ReactNode }) {
  return (
    <span
      data-mail-address=""
      className="inline-block max-w-full [hyphens:none] [overflow-wrap:anywhere] [word-break:break-all]"
    >
      {children}
    </span>
  );
}

export const MailStandingBanner = observer(function MailStandingBanner() {
  const t = useT();
  const format = useFormat();
  const { authStore } = useStores();
  // The first commit draws from the seed: an effect would draw one frame without the bar.
  const [standing, setStanding] = useState<IMailStanding | null>(
    () => authStore.user?.mailStanding ?? null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const userId = authStore.user?.id;
  const isVerificationSession = Boolean(authStore.isVerificationSession);
  // I-1405: `/auth/me` carries this banner's whole answer, so the first commit already draws it and
  // the shell could hold the page for the chunk. Read through a ref: a later session refresh must not
  // reset the "Sent to" confirmation.
  const seed = authStore.user?.mailStanding;
  const seedRef = useRef(seed);
  seedRef.current = seed;
  const scroller = useBottomScrollFade<HTMLDivElement>();

  useEffect(() => {
    setSent(false);
    setError(null);
    if (!userId || isVerificationSession) {
      setStanding(null);
      return;
    }
    if (seedRef.current !== undefined) {
      setStanding(seedRef.current ?? null);
      return;
    }
    setStanding(null);
    let cancelled = false;
    authApi
      .mailStanding()
      .then((answer) => {
        if (!cancelled) setStanding(answer);
      })
      .catch(() => {
        // Silent on purpose: a reminder must not block the screen below it, and "Send again" is not
        // the only way to learn the state — a failed load here simply shows nothing.
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isVerificationSession]);

  if (dismissed || isDrawerPanelView()) return null;
  if (!sent && !isStoppedStanding(standing)) return null;

  async function sendAgain() {
    setBusy(true);
    setError(null);
    try {
      setStanding(await authApi.sendMailAgain());
      setSent(true);
    } catch (err) {
      setError(actionFailureTextIn(t, err, t('auth.actions.resendMessage')));
    } finally {
      setBusy(false);
    }
  }

  const address = standing?.address ?? '';
  const complained = standing?.reason === 'complained';

  return (
    <ViewportBottomBar order="account">
      <div
        ref={scroller.ref}
        role="status"
        data-bottom-bar="mail-standing"
        /* The words may be long at the largest text size: the bar keeps at most 40% of the window and scrolls inside it, takes focus while it scrolls and fades its bottom edge while more follows, as `EmailVerificationBanner` does (I-0855, I-1450). */
        tabIndex={scroller.overflowing ? 0 : undefined}
        onFocus={scroller.onFocus}
        onKeyDown={scroller.onKeyDown}
        className={`flex ${BOTTOM_BAR_ROW} max-h-[40dvh] flex-wrap items-center gap-2.5 overflow-y-auto border-t border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-950 [overflow-wrap:anywhere] dark:border-amber-700/60 dark:bg-[#2b2618] dark:text-amber-100 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-amber-700 dark:focus-visible:outline-amber-300`}
      >
        {/*
        The icon sits in the words' own line instead of a column beside them: at the largest text size
        a column took 30 px of the line, and an address that fits the whole line broke in its middle
        (measured at 390, 312%: 344 px of 358).
      */}
        {/* I-1905 (QA 505): on a phone the words take the whole width and the actions go below them. */}
        <span
          className="min-w-[min(100%,14em)] flex-1 max-sm:basis-full"
          data-mail-standing-words=""
        >
          {sent && standing?.reason === null ? (
            <CheckCircle2
              className="mr-2 inline-block h-4 w-4 shrink-0 align-text-bottom"
              aria-hidden="true"
            />
          ) : (
            <MailWarning
              className="mr-2 inline-block h-4 w-4 shrink-0 align-text-bottom"
              aria-hidden="true"
            />
          )}
          <span className="min-w-0">
            {sent && standing?.reason === null ? (
              <>
                <span className="font-medium">
                  {t.rich('banners.mail.sentTo', {
                    email: address,
                    address: (chunks: ReactNode) => <MailAddress>{chunks}</MailAddress>,
                  })}
                </span>{' '}
                <span className="text-amber-800 dark:text-amber-200">
                  {t('banners.mail.checkInbox')}
                </span>
              </>
            ) : complained ? (
              <>
                <span className="font-medium">
                  {t.rich('banners.mail.notSending', {
                    email: address,
                    address: (chunks: ReactNode) => <MailAddress>{chunks}</MailAddress>,
                  })}
                </span>{' '}
                <span className="text-amber-800 dark:text-amber-200">
                  {t('banners.mail.complained')}
                </span>
              </>
            ) : (
              <>
                <span className="font-medium">
                  {t.rich(
                    standing?.bouncedAgain
                      ? 'auth.mailStanding.bouncedAgain'
                      : 'auth.mailStanding.bounced',
                    {
                      email: address,
                      address: (chunks: ReactNode) => <MailAddress>{chunks}</MailAddress>,
                    },
                  )}
                </span>{' '}
                <span className="text-amber-800 dark:text-amber-200">
                  {t('banners.mail.bouncedDetail')}
                  {standing?.canSendAgain === false && standing.retryAt
                    ? t('banners.mail.tooOften', {
                        when: format.date(new Date(standing.retryAt), {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        }),
                      })
                    : ''}
                </span>
              </>
            )}
            {error ? (
              <span role="alert" className="mt-0.5 block text-red-700 dark:text-red-300">
                {error}
              </span>
            ) : null}
          </span>
        </span>
        {standing?.reason === 'bounced' ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={busy}
            disabled={!standing.canSendAgain}
            onClick={() => void sendAgain()}
          >
            {t('banners.mail.sendAgain')}
          </Button>
        ) : null}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="rounded px-2 py-1 text-amber-700 evx-link-hover dark:text-amber-300"
        >
          {t('banners.dismiss')}
        </button>
        <BottomScrollFade
          overflowing={scroller.overflowing}
          moreBelow={scroller.moreBelow}
          fromClassName="from-amber-50 dark:from-[#2b2618]"
        />
      </div>
    </ViewportBottomBar>
  );
});
